import React, { useState } from 'react';
import { Alert, Button, Container, Row, Col, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import { supabase } from '../supabase/supabase';

export function RequestResetForm() {
    const [email, setEmail] = useState('');
    const [isSuccess, setIsSuccess] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        
        try {
            const { error } = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: `${window.location.origin}/reset-password`,
            });

            if (error) throw error;
            
            setIsSuccess(true);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="auth-page">
            <Header />
            
            <div className="auth-container">
                <Container>
                    <Row className="justify-content-center align-items-center min-vh-80">
                        <Col lg={5} md={7} sm={9}>
                            <div className="auth-card">
                                <div className="auth-header">
                                    <h2 className="auth-title">Reset Password</h2>
                                    <p className="auth-subtitle">
                                        Enter your email address and we'll send you a link to reset your password
                                    </p>
                                </div>
                                
                                {isSuccess ? (
                                    <div className="success-message">
                                        <h4>Check your email!</h4>
                                        <p>We've sent you a password reset link.</p>
                                    </div>
                                ) : (
                                    <form onSubmit={handleSubmit} className="modern-auth-form">
                                        {error && <Alert variant="danger" className="auth-error">{error}</Alert>}
                                        
                                        <div className="form-group">
                                            <input
                                                type="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                required
                                                className="modern-input"
                                                placeholder="Email address"
                                            />
                                        </div>

                                        <Button 
                                            type="submit" 
                                            disabled={isLoading}
                                            className="modern-submit-btn"
                                            size="lg"
                                        >
                                            {isLoading ? 'Sending...' : 'Send Reset Link'}
                                        </Button>
                                    </form>
                                )}
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>
        </div>
    );
}

export function ResetPasswordForm() {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isSuccess, setIsSuccess] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    const validate = () => {
        const v_errors:string[] = [];
        const regex = /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*\W)/;
        
        if (password.length < 8) {
            v_errors.push('Password must be at least 8 characters long.');
        }
        if (!regex.test(password)) {
            v_errors.push('Password must contain at least one lowercase letter, one uppercase letter, one number, and one symbol.');
        }
        if (password !== confirmPassword) {
            v_errors.push('Passwords do not match.');
        }
        
        setErrors(v_errors);
        return v_errors.length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;
        
        setIsLoading(true);
        setErrors([]);

        try {
            const { error } = await supabase.auth.updateUser({
                password: password
            });

            if (error) throw error;
            
            setIsSuccess(true);
            setTimeout(() => navigate('/dashboard'), 2000);
        } catch (err: any) {
            setErrors([err.message]);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="auth-page">
            <Header />
            
            <div className="auth-container">
                <Container>
                    <Row className="justify-content-center align-items-center min-vh-80">
                        <Col lg={5} md={7} sm={9}>
                            <div className="auth-card">
                                <div className="auth-header">
                                    <h2 className="auth-title">Set New Password</h2>
                                    <p className="auth-subtitle">
                                        Choose a strong password for your account
                                    </p>
                                </div>
                                
                                {isSuccess ? (
                                    <div className="success-message">
                                        <h4>Password Updated!</h4>
                                        <p>Redirecting to your dashboard...</p>
                                    </div>
                                ) : (
                                    <form onSubmit={handleSubmit} className="modern-auth-form">
                                        {errors.length > 0 && (
                                            <Alert variant="danger" className="auth-error">
                                                <ul className="error-list">
                                                    {errors.map((error, index) => (
                                                        <li key={index}>{error}</li>
                                                    ))}
                                                </ul>
                                            </Alert>
                                        )}
                                        
                                        <div className="form-group">
                                            <input
                                                type="password"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                required
                                                className="modern-input"
                                                placeholder="New password"
                                            />
                                        </div>

                                        <div className="form-group">
                                            <input
                                                type="password"
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                required
                                                className="modern-input"
                                                placeholder="Confirm new password"
                                            />
                                        </div>

                                        <Button 
                                            type="submit" 
                                            disabled={isLoading}
                                            className="modern-submit-btn"
                                            size="lg"
                                        >
                                            {isLoading ? 'Updating...' : 'Update Password'}
                                        </Button>
                                    </form>
                                )}
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>
        </div>
    );
}
